/**
 * Patient List Page
 * ==================
 * List and search patients with infinite scroll.
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Search,
  Plus,
  Filter,
  Users,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

import { listPatients } from '@/api/patients';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { formatDate, getRiskLevelClass } from '@/lib/utils';
import type { PatientBrief } from '@/types';

// =============================================================================
// COMPONENT
// =============================================================================

export default function PatientList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const pageSize = 20;
  const queryParam = searchParams.get('q') || undefined;

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['patients', { query: queryParam }],
    queryFn: ({ pageParam = 1 }) =>
      listPatients({
        query: queryParam,
        page: pageParam,
        page_size: pageSize,
      }),
    getNextPageParam: (lastPage) => {
      // Use the has_next from pagination metadata
      return lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined;
    },
    initialPageParam: 1,
  });

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: searchQuery });
  };

  // Flatten all pages into a single array
  const allPatients = data?.pages.flatMap((page) => page.items) || [];
  const totalCount = data?.pages[0]?.pagination?.total_items || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patients</h1>
          <p className="text-gray-600">
            Manage your patient records
          </p>
        </div>
        <Link to="/patients/add">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Add Patient
          </Button>
        </Link>
      </div>

      {/* Search & Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or chart number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
          <Button type="button" variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </form>
      </Card>

      {/* Patient List */}
      <Card>
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <p className="text-gray-600">Failed to load patients</p>
          </div>
        ) : allPatients.length > 0 ? (
          <>
            <div className="divide-y divide-gray-200">
              {allPatients.map((patient) => (
                <PatientRow key={patient.id} patient={patient} />
              ))}
            </div>
            
            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="px-6 py-4 border-t border-gray-200">
              {isFetchingNextPage ? (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading more patients...
                </div>
              ) : hasNextPage ? (
                <div className="text-center text-sm text-gray-500">
                  Scroll for more patients
                </div>
              ) : (
                <div className="text-center text-sm text-gray-500">
                  Showing all {totalCount} patients
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600 mb-4">
              {queryParam
                ? 'No patients found matching your search'
                : 'No patients yet'}
            </p>
            <Link to="/patients/add">
              <Button>Add your first patient</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface PatientRowProps {
  patient: PatientBrief;
}

function PatientRow({ patient }: PatientRowProps) {
  return (
    <Link
      to={`/patients/${patient.id}`}
      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
          <span className="text-primary-700 font-medium text-lg">
            {patient.first_name[0]}
            {patient.last_name[0]}
          </span>
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {patient.full_name}
          </p>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            {patient.chart_number && (
              <span>#{patient.chart_number}</span>
            )}
            {patient.date_of_birth && (
              <span>DOB: {formatDate(patient.date_of_birth)}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {patient.last_perio_exam_date && (
          <div className="text-sm text-right">
            <p className="text-gray-500">Last Exam</p>
            <p className="font-medium">{formatDate(patient.last_perio_exam_date)}</p>
          </div>
        )}
        
        {patient.periodontal_risk_level && (
          <Badge className={getRiskLevelClass(patient.periodontal_risk_level)}>
            {patient.periodontal_risk_level} risk
          </Badge>
        )}

        {!patient.is_active && (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </div>
    </Link>
  );
}
